import { Component, computed, inject, signal, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';

import { Auth } from '../../../services/auth';
import { CommentService } from '../../../services/comment';
import { EesztAuthService } from '../../../services/eeszt-auth.service';
import { EesztService } from '../../../services/eeszt.service';
import { environment } from '../../../../environments/environment';
import { EesztAuthDialog } from '../../eeszt-auth-dialog/eeszt-auth-dialog';
import { CountdownPipe } from '../../../pipes/countdown.pipe';

export interface NavbarButton {
  id: string;
  icon: string;
  title: string;
  action: () => void;
  visible: () => boolean;
  buttonType: 'mini-fab' | 'flat-button';
  classes?: string[];
  badge?: {
    value: () => number;
    hidden: () => boolean;
    position?: 'before' | 'after' | 'above' | 'below';
    color?: 'primary' | 'accent' | 'warn';
  };
}

@Component({
  selector: 'app-navbar',
  imports: [CommonModule, MatButtonModule, MatIconModule, MatToolbarModule, MatBadgeModule, NgOptimizedImage, CountdownPipe],
  templateUrl: './navbar.html',
  styleUrl: './navbar.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Navbar implements OnInit {
  private authService = inject(Auth);
  private router = inject(Router);
  private commentService = inject(CommentService);
  private eesztAuthService = inject(EesztAuthService);
  private eesztService = inject(EesztService);
  private dialog = inject(MatDialog);
  private toastr = inject(ToastrService);

  logoUrl = environment.navbarLogoUrl;

  // EESZT signals from services
  eszigTokenValid = this.eesztAuthService.isAuthenticated;
  remainingSeconds = this.eesztAuthService.remainingSeconds;
  bcpMessageCount = computed(() => 0); // Will be loaded on init

  // Comment count from service (filtered by referral visibility)
  commentLength = computed(() => {
    const isSender = this.authService.isSender();
    return this.commentService.comments()
      .filter(c => !c.deleted && c.referral === isSender)
      .length;
  });

  // Computed properties
  currentUser = computed(() => this.authService.authState().user);
  username = computed(() => this.currentUser()?.name || this.currentUser()?.username || 'User');

  // Role-based computed properties
  isAdmin = computed(() => this.authService.isAdmin());
  isReferring = computed(() => this.authService.isSender());
  isPathologist = computed(() => this.authService.isReportingDoctorOrAssistant());
  isReportingDoctor = computed(() => this.authService.isReportingDoctor());
  isQualityControl = computed(() => this.authService.isQualityControl());

  // Route-based computed properties  
  currentRoute = signal(this.router.url);
  isAdminRoute = computed(() => this.currentRoute().includes('admin'));
  isReportsRoute = computed(() => this.currentRoute().includes('/reports/'));
  showNewReport = computed(() =>
    this.currentRoute() === '/reports'
  );

  constructor() {
    // Listen to router navigation events and update the currentRoute signal
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentRoute.set(event.url);
      });
  }

  // EESZT visibility logic from original template
  showEeszt = computed(() =>
    !!this.isPathologist() && !!this.eszigTokenValid()
  );

  // Programmatic button configurations
  navbarButtons = computed<NavbarButton[]>(() => [
    {
      id: 'admin',
      icon: 'admin_panel_settings',
      title: 'Admin',
      action: () => this.navigateTo('/admin'),
      visible: () => this.isAdmin(),
      buttonType: 'mini-fab'
    },
    {
      id: 'comments',
      icon: 'mail',
      title: 'Megjegyzések',
      action: () => this.openHistory(),
      visible: () => !!this.isReportsRoute(),
      buttonType: 'mini-fab',
      badge: {
        value: () => this.commentLength(),
        hidden: () => this.commentLength() === 0,
        position: 'before',
        color: 'accent'
      }
    },
    {
      id: 'referring-worklist',
      icon: this.showNewReport() ? 'send' : 'view_headline',
      title: this.showNewReport() ? 'Feladás' : 'Munkalista',
      action: () => this.goToPageWithCheckRoute(),
      visible: () => this.isReferring(),
      buttonType: 'mini-fab'
    },
    {
      id: 'pathologist-worklist',
      icon: 'view_headline',
      title: 'Munkalista',
      action: () => this.navigateTo('reports'),
      visible: () => !!this.isPathologist(),
      buttonType: 'mini-fab'
    },
    {
      id: 'quality-control',
      icon: 'fact_check',
      title: 'Minőségellenőrzés',
      action: () => this.navigateTo('/reports/quality-control'),
      visible: () => !!this.isQualityControl() && this.currentRoute() === '/reports',
      buttonType: 'mini-fab'
    },
    {
      id: 'bcp-messages-with-count',
      icon: 'priority_high',
      title: 'EESZT kommunikációs napló',
      action: () => this.goToEeszt(),
      visible: () => !!this.isPathologist() && !!this.eszigTokenValid() && this.bcpMessageCount() > 0,
      buttonType: 'flat-button',
      classes: ['hasBcpMessage']
    },
    {
      id: 'bcp-messages-no-count',
      icon: 'done',
      title: 'EESZT kommunikációs napló',
      action: () => this.goToEeszt(),
      visible: () => !!this.isPathologist() && !!this.eszigTokenValid() && this.bcpMessageCount() === 0,
      buttonType: 'mini-fab',
      classes: ['hasNoBcpMessage']
    },
    {
      id: 'security',
      icon: 'lock',
      title: 'Biztonsági beállítások',
      action: () => this.navigateTo('/security'),
      visible: () => true,
      buttonType: 'mini-fab'
    }
  ]);

  ngOnInit() {
    // Component initialization
  }

  goToHomePage(): void {
    const user = this.currentUser();
    if (!user?.role?.code.includes('ADMIN')) {
      this.router.navigate(['/reports']);
    } else {
      this.router.navigate(['/admin']);
    }
  }

  navigateTo(url: string): void {
    this.router.navigate([url]);
  }

  openHelp(): void {
    window.open('/help', '_blank');
  }

  openHistory(): void {
    this.commentService.toggle();
  }

  goToPageWithCheckRoute(): void {
    if (this.currentRoute() === '/reports') {
      this.router.navigate(['/reports/request/new']);
    } else {
      this.router.navigate(['/reports']);
    }
  }

  openEesztAuthModal(): void {
    const dialogRef = this.dialog.open(EesztAuthDialog, {
      width: '1200px',
      maxWidth: '95vw',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true || result === 'skip') {
        this.router.navigate(['/eeszt']);
      }
    });
  }

  goToEeszt(): void {
    if (!this.eszigTokenValid()) {
      this.toastr.error('EESZT autentikáció lejárt');
      return;
    }
    this.router.navigate(['/eeszt']);
  }

  logout(): void {
    this.authService.logout().subscribe(() => {
      this.router.navigate(['/']);
    });
  }
}
